import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { FormErrorMessage } from '@/components/auth/FormErrorMessage'
import { useConfirmPasswordReset } from '@/hooks/api/useAuth'

export const Route = createFileRoute('/(onboarding)/(auth)/reset-password')({
    validateSearch: (search) => ({
        oobCode: typeof search.oobCode === 'string' ? search.oobCode : '',
    }),
    component: ResetPasswordRoute,
})

function ResetPasswordRoute() {
    const { oobCode } = useSearch({
        from: '/(onboarding)/(auth)/reset-password',
    })
    const navigate = useNavigate()
    const {
        mutate: confirmReset,
        isPending: isConfirming,
        error: resetError,
    } = useConfirmPasswordReset()

    const schema = z
        .object({
            password: z
                .string()
                .min(6, 'Password must be at least 6 characters'),
            confirmPassword: z.string().min(6, 'Confirm password is required'),
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: 'Passwords do not match',
            path: ['confirmPassword'],
        })

    type FormData = z.infer<typeof schema>

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(schema),
    })

    const onSubmit = useCallback(
        (data: FormData) => {
            confirmReset(
                { oobCode, newPassword: data.password },
                {
                    onSuccess: () => navigate({ to: '/login' }),
                }
            )
        },
        [confirmReset, navigate, oobCode]
    )

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Reset password
                    </h1>
                    <p className="mt-3 text-sm text-white">
                        Enter your new password below.
                    </p>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="flex flex-col gap-4"
                    >
                        <div>
                            <GlossyInput
                                {...register('password')}
                                type="password"
                                placeholder="New password"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                            />
                            {errors.password && (
                                <FormErrorMessage
                                    message={errors.password.message || ''}
                                />
                            )}
                        </div>
                        <div>
                            <GlossyInput
                                {...register('confirmPassword')}
                                type="password"
                                placeholder="Confirm password"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                            />
                            {errors.confirmPassword && (
                                <FormErrorMessage
                                    message={
                                        errors.confirmPassword.message || ''
                                    }
                                />
                            )}
                        </div>
                        {resetError && (
                            <div className="mt-2 text-center text-sm text-red-400">
                                {(resetError as Error).message}
                            </div>
                        )}
                    </form>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="pt-6">
                        <GlossyButton
                            type="submit"
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleSubmit(onSubmit)}
                            disabled={isConfirming}
                        >
                            {isConfirming ? 'Resetting...' : 'Reset Password'}
                        </GlossyButton>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
